import { Link, useParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import useCartStore from '../../stores/cart.store';
import { formatMoney } from '../../utils/Utils';
import CheckoutFab from '../../components/layout/CheckoutFab';
import ImageCarousel from './ImageCarousel';
import { useProductQuery } from '@/hooks/queries/use-product-query';
import CartButton from '@/components/products/CartButton';
import usePollOrderValidation from '@/hooks/use-poll-order-validation';
import { Spinner } from '@/components/spinner';
import { CircleXIcon } from 'lucide-react';
import { Markdown } from '@/components/markdown';

const ProductDetails = () => {
  usePollOrderValidation();
  const { id: productId } = useParams();
  const { addItem, updateQuantity } = useCartStore();
  const item = useCartStore((state) => state.items).find((item) => item.id === productId);
  const { data: product, isPending, error } = useProductQuery(productId!);

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen flex-col gap-2">
        <CircleXIcon color="red" size={40} />
        <h3 className="text-lg leading-relaxed">Ett oväntat fel uppstod {error?.message}</h3>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      {/* Sidebar */}

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {/*  Site header */}
        <Header variant="default" />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full mb-12">
            {/* Page content */}
            <div className="max-w-5xl mx-auto flex flex-col lg:flex-row lg:space-x-8 xl:space-x-16">
              {/* Content */}
              <div className="flex-1">
                <div className="mb-3">
                  <Link
                    to=".."
                    className="text-sm font-medium text-violet-500 hover:text-violet-600 dark:hover:text-violet-400"
                  >
                    &lt;- Tillbaka till produkterna
                  </Link>
                </div>
                <header className="mb-4">
                  {/* Title */}
                  <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold mb-2">
                    {product.name}
                  </h1>
                  <p>{product.tagline}</p>
                </header>

                {/* Image */}
                <figure className="mb-6">
                  <ImageCarousel assets={product.media} />
                </figure>
              </div>

              <div className="flex-1">
                {/* Product content */}
                <div className="mb-4 flex items-center gap-4 justify-start">
                  <div className="text-2xl text-gray-800 dark:text-gray-100 font-semibold">
                    {formatMoney(product.price)}
                  </div>
                  <hr className="w-px h-10 bg-gray-200 dark:bg-gray-700/60 border-none" />
                  <div className="max-w-48">
                    <CartButton
                      onChange={(value) => updateQuantity(product.id, value)}
                      value={item?.quantity}
                      addToCart={() =>
                        addItem({
                          id: product.id,
                          name: product.name,
                          tagline: product.tagline,
                          price: product.price,
                          thumbnail: product.media.find((m) => m.primary)?.renditions.THUMBNAIL as any,
                        })
                      }
                    />
                  </div>
                  {/* AddToCartButton */}
                </div>
                <h2 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-2">Beskrivning</h2>
                <Markdown content={product.description} />
              </div>
            </div>
          </div>
          <CheckoutFab />
        </main>
      </div>
    </div>
  );
};

export default ProductDetails;
